import { useEffect, useRef, type RefObject } from "react";

const MAX_FRAMES = 30; // ~0.5s at 60fps — a hard stop so a box that never
// stops resizing (or an environment that never lays anything out, e.g.
// jsdom) still runs the callback instead of stranding focus forever.

function sameRect(a: DOMRect, b: DOMRect): boolean {
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

/**
 * Runs `onSettled` once the element `boundsRef` points at has stopped
 * moving/resizing across two consecutive animation frames — the point a
 * CSS entrance transition (scale/opacity) has finished placing it, whether
 * or not one is actually running (resolves after two frames when nothing
 * animates: prefers-reduced-motion, or a test environment with no real
 * layout).
 *
 * Exists so a popover/dialog never has to focus an input synchronously on
 * open — doing that before the box has settled in the viewport is what
 * triggers iOS Safari's keyboard-open viewport glitch. `animationend`
 * alone isn't a fix either: it simply never fires when there's no
 * transition, leaving focus stuck (the bug this replaces).
 */
export function useFocusOnceSettled(
  active: boolean,
  boundsRef: RefObject<HTMLElement | null>,
  onSettled: () => void
): void {
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useEffect(() => {
    if (!active) return;

    let raf = 0;
    let cancelled = false;
    let lastRect: DOMRect | null = null;
    let identicalConsecutiveFrames = 0;

    const tick = (attempt: number) => {
      if (cancelled) return;
      const el = boundsRef.current;
      if (!el) return;
      
      const rect = el.getBoundingClientRect();
      if (lastRect !== null && sameRect(rect, lastRect)) {
        identicalConsecutiveFrames++;
      } else {
        identicalConsecutiveFrames = 0;
      }
      
      lastRect = rect;

      // Require 4 consecutive identical frames to confidently declare it settled.
      // This bridges the 1-2 frame gap before CSS animations actually begin 
      // interpolating in the browser, while still resolving very quickly (~66ms) 
      // if there's no animation at all.
      const settled = identicalConsecutiveFrames >= 4;

      if (settled || attempt >= MAX_FRAMES) {
        onSettledRef.current();
        return;
      }
      raf = requestAnimationFrame(() => tick(attempt + 1));
    };

    raf = requestAnimationFrame(() => tick(0));
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [active, boundsRef]);
}
